
/// <amd-module name="Wit/BoardsHub/KanbanBoard/SignalR/KanbanBoardSignalRService" />
import { IVssPageContext, IVssService, VssService, Services } from "VSS/Platform/Context";
import { IVssHistoryService } from "VSS/Platform/History";
import { IHubHostInformation } from "VSS/Features/SignalR/Hub";
import { IVssPageService } from "VSS/Platform/Page";
import { traceException } from "VSS/Platform/Trace";
import { IKanbanBoardSignalROptions, KanbanBoardSignalRHub } from "./KanbanBoardSignalRHub";
import { IBoardUpdatePayload, BoardWorkItemsUpdatedCallback, BoardSettingsUpdatedCallback } from "./Contracts";

export const IKanbanBoardSignalRServiceId = "IKanbanBoardSignalRService";

const workItemsUpdatedDebounceMs = 1500;
const boardSettingsUpdatedDebounceMs = 1000;

export interface IKanbanBoardSignalRService extends IVssService {
    watch(
        boardExtensionId: string,
        onWorkItemsUpdated: (updatePayload: IBoardUpdatePayload[]) => void,
        onCommonSettingsChanged: () => void
    ): Promise<void>;

    endWatch(boardExtensionId: string): Promise<void>;
}

class KanbanBoardSignalRService extends VssService implements IKanbanBoardSignalRService {
    private hub: KanbanBoardSignalRHub | null;

    public _serviceEnd(pageContext: IVssPageContext): void {
        super._serviceEnd(pageContext);
        this.hub?.stop();
        this.hub = null;
    }

    public async watch(
        boardExtensionId: string,
        onWorkItemsUpdated: BoardWorkItemsUpdatedCallback,
        onCommonSettingsChanged: BoardSettingsUpdatedCallback
    ): Promise<void> {
        this._ensureHubInitialized();

        let settingsTimeoutHandle: number | null = null;
        let workItemsTimeoutHandle: number | null = null;
        let aggregatedUpdates: IBoardUpdatePayload[] = [];

        function aggregatedDebouncedOnWorkItemsUpdated(updatePayload: IBoardUpdatePayload[]) {
            for (const update of updatePayload) {
                const matchingItemIndex = aggregatedUpdates.findIndex(u => u.id === update.id);
                if (matchingItemIndex === -1) {
                    // We do not have an update for this work item yet
                    aggregatedUpdates.push(update);
                    continue;
                }

                if (update.revision > aggregatedUpdates[matchingItemIndex].revision) {
                    // This update is more recent than the one already in the update list; replace it
                    aggregatedUpdates[matchingItemIndex] = update;
                    continue;
                }
            }

            workItemsTimeoutHandle && clearTimeout(workItemsTimeoutHandle);
            workItemsTimeoutHandle = setTimeout(() => {
                onWorkItemsUpdated(aggregatedUpdates);
                workItemsTimeoutHandle = null;
                aggregatedUpdates = [];
            }, workItemsUpdatedDebounceMs);
        }

        function debouncedOnCommonSettingsChanged() {
            settingsTimeoutHandle && clearTimeout(settingsTimeoutHandle);
            settingsTimeoutHandle = setTimeout(() => {
                onCommonSettingsChanged();
                settingsTimeoutHandle = null;
            }, boardSettingsUpdatedDebounceMs);
        }

        try {
            await this.hub!.watch(boardExtensionId, aggregatedDebouncedOnWorkItemsUpdated, debouncedOnCommonSettingsChanged);
        } catch (error) {
            traceException(this.pageContext, "WIT", "WorkItem", error, { ["method"]: "SignalR.Watch" });
        }
    }

    public async endWatch(boardId: string): Promise<void> {
        try {
            await this.hub?.endWatch(boardId);
        } catch (error) {
            traceException(this.pageContext, "WIT", "WorkItem", error, { ["method"]: "SignalR.EndWatch" });
        }
    }

    private _ensureHubInitialized() {
        if (this.hub) {
            return;
        }

        const vssPageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        if (!vssPageData) {
            throw new Error("No page data available");
        }

        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        const hostInformation: IHubHostInformation = {
            hostId: vssPageData.hostId,
            hostPath: vssPageData.hostPath,
            isHosted: vssPageData.isHosted
        };
        const options: IKanbanBoardSignalROptions = {
            hostInformation: hostInformation,
            historyService: historyService,
            pageContext: this.pageContext,
            useSignalRAppPoolDefaultValue: true
        };
        this.hub = new KanbanBoardSignalRHub(options);
    }
}

Services.add(IKanbanBoardSignalRServiceId, { serviceFactory: KanbanBoardSignalRService });
